import { inject, Injectable } from '@angular/core';
import { catchError, defer, Observable, of, switchMap, tap, finalize, throwError } from 'rxjs';
import { Store } from '@lib.core/store/store.service';
import { cleanError } from '@lib.core/error/clean-error';

@Injectable({
  providedIn: 'root',
})
export class Cachier {
  private readonly store = inject(Store);
  private readonly defaultExpiryMs: number = 5 * 60 * 1000;
  private readonly abandoned = new Set<string>();

  /**
   * Reads a key, requesting it through `callBack` unless it holds a fresh value or a request for it is on the way.
   * A reader that joins a request on the way fails with it, as its first reader does, and requests the key itself when
   * that reader leaves before the response; a reader that already has a value keeps it when a later refresh fails.
   */
  get<T>(key: string, callBack: () => Observable<T>, bust?: boolean, ttl?: number): Observable<T | null | undefined> {
    if (bust || !this.contains(key)) {
      this.abandoned.delete(key);
      this.store.register(key).setValue<T>(key, undefined);
      let isResponseReceived = false;

      return callBack().pipe(
        tap(() => isResponseReceived = true),
        tap(() => this.store.setExpiryMs(key, ttl ?? this.defaultExpiryMs)),
        tap(m => this.store.setValue<T>(key, m)),
        catchError(error => {
          this.store.setError(key, cleanError(error).message);
          throw error;
        }),
        finalize(() => {
          if (!isResponseReceived) {
            this.abandoned.add(key);
            this.purge(key);
          }
        })
      );
    }
    return defer(() => {
      let hasValue = false;
      return this.store.getValue<T>(key).pipe(
        switchMap((value: T | null | undefined): Observable<T | null | undefined> => {
          if (value !== null && value !== undefined) {
            hasValue = true;
          } else if (!hasValue && value === null && this.store.hasError(key)) {
            return throwError(() => new Error(this.store.getError(key)));
          } else if (!hasValue && value === null && this.abandoned.has(key)) {
            return this.get(key, callBack, false, ttl);
          }
          return of(value);
        }),
      );
    });
  }

  contains(key: string): boolean {
    return this.store.hasValidValue(key) && !this.store.isExpired(key);
  }

  hasValue(key: string): boolean {
    return this.store.hasSettledValue(key);
  }

  peek<T>(key: string): Observable<T | null | undefined> {
    return this.store.getValue<T>(key);
  }

  /**
   * Refreshes without clearing first, so readers keep seeing the previous value. `get(key, cb, true)`
   * cannot: it writes `undefined` before the request and purges the key if the subscriber leaves
   * before the response.
   */
  revalidate<T>(key: string, callBack: () => Observable<T>, ttl?: number): Observable<T> {
    return callBack().pipe(
      tap(value => {
        this.store.setValue<T>(key, value);
        this.store.setExpiryMs(key, ttl ?? this.defaultExpiryMs);
      }),
    );
  }

  purge(key: string) {
    this.store.removeKey(key);
  }
}
